import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import {
  fetchWithTokenAndRegion,
  getAdminConstructionApiUrl,
} from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES, useSWRAlloy } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';

export type CurrentAccount = {
  id: string;
  name: string;
  imageUrl: string;
  accessLevels: {
    accountAdministrator: boolean;
    accountStandardsAdministrator: boolean;
    accountExecutive: boolean;
  };
  accountEntitledFeatureKeys?: string[];
  userEntitledFeatureKeys?: string[];
  products: {
    /** The type of product */
    key: string;
    /** The id of the resource associated to this group */
    category: string;
    status: string;
    /** The display name of the product */
    name: string;
    /** The url for the product icon */
    iconUrl: string;
    /** Boolean indicating of the user is authorized to access this product */
    authorized: boolean;
    /** The url for the default tool in this product. Will default to remediationUrl if user is not authorized */
    defaultUrl: string;
    /** The product access levels */
    accessLevels: {
      /** Indicates the user is an administrator of the product */
      productAdministrator: boolean;
      /** Indicates the user is a member of the product */
      productMember: boolean;
    };
    /** List of tools available in the product */
    tools: {
      /** The type of tool */
      key: string;
      /** The display name of the tool */
      name: string;
      /** The url for the tool icon */
      iconUrl?: string | null;
      /** The url of the tool */
      url: string;
    }[];
  }[];
};

type CurrentAccountEntitlementsContextValue = {
  loading?: boolean;
  error?: unknown;
  retry: () => void;
  currentAccount: CurrentAccount;
  setCurrentAccount: (nextAccount: CurrentAccount) => void;
};

export const CurrentAccountEntitlementsContext =
  getOrCreateGlobalContext<CurrentAccountEntitlementsContextValue>(
    CONTEXT_NAMES.CurrentAccountEntitlementsContext,
  );

export const useCurrentAccountEntitlements = () =>
  useContextWithValidation(CurrentAccountEntitlementsContext, {
    hook: 'useCurrentAccountEntitlements',
    provider: 'CurrentAccountEntitlementsProvider',
  });

export function CurrentAccountEntitlementsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [currentAccount, setCurrentAccount] = useState<
    CurrentAccount | undefined
  >(undefined);

  const accountId = useCurrentAccountId();

  useProviderOnlyOnce({
    context: CurrentAccountEntitlementsContext,
    contextName: CONTEXT_NAMES.CurrentAccountEntitlementsContext,
  });

  const url = useMemo(() => {
    if (!accountId) {
      return null;
    }

    return `${getAdminConstructionApiUrl()}/internal/v1/account-entitlements/${accountId}`;
  }, [accountId]);

  const {
    data,
    error,
    revalidate: retry,
    clearSessionStorage,
  } = useSWRAlloy(url, fetchWithTokenAndRegion);

  const setCurrentAccountAndClearCache = useCallback(
    (nextAccount: CurrentAccount) => {
      setCurrentAccount(nextAccount);
      clearSessionStorage();
    },
    [setCurrentAccount, clearSessionStorage],
  );

  const value = useMemo(
    () => ({
      loading: !error && !data,
      error,
      retry,
      setCurrentAccount: setCurrentAccountAndClearCache,
      currentAccount: currentAccount || data,
    }),
    [error, data, currentAccount, retry, setCurrentAccountAndClearCache],
  );

  return (
    <CurrentAccountEntitlementsContext.Provider value={value}>
      {children}
    </CurrentAccountEntitlementsContext.Provider>
  );
}
